/* ────────────────────────────────────────────────────────── */
/*  src/api/client.js - COMPLETE FIXED VERSION               */
/*  ✅ Fixed user verification and assessment settings calls  */
/*  ✅ Added timer settings support (min_question_time)       */
/*  ✅ Added timer storage in performance records             */
/* ────────────────────────────────────────────────────────── */

import { assessmentIdOf, isEnabledFlag } from '../utils/assessmentIdentity';
import { boundedOverallTimeSeconds, normalizeTimerMode } from '../utils/assessmentTiming';
import {
  acknowledgeProtectedResponses,
  clearProtectedResponses,
  listProtectedResponses,
  markProtectedResponseAttempt,
  publishResponseSyncStatus,
  queueProtectedResponses,
  readProtectedResponse
} from '../utils/responseOutbox';

// Use the origin that delivered the application. Production and isolated
// staging each proxy /api to their own backend, so a staging browser can never
// be redirected into the production API by a compiled absolute URL.
const API_BASE_URL = '/api';

// v8.12.7: keep progress writes ordered per student session. A delayed request
// must never overtake a newer navigation/heartbeat request on a shared tablet.
const sessionProgressQueues = new Map();
const assessmentItemStateQueues = new Map();
const RETRYABLE_PROGRESS_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);
const SESSION_BOUNDARY_CODES = new Set([
  'TOKEN_EXPIRED',
  'INVALID_SESSION',
  'SESSION_REVOKED',
  'SESSION_IDLE_TIMEOUT',
  'ACCOUNT_EXPIRED',
  'ACCOUNT_RESTRICTED',
  'ACCOUNT_UNAVAILABLE',
  'ACCOUNT_POLICY_INVALID'
]);
let logoutPromise = null;
let authBoundaryTransitionStarted = false;

const wait = (milliseconds) => new Promise(resolve => setTimeout(resolve, milliseconds));

async function sendSessionProgress({ continueCode, accessCode, questionIndex, activityType, viewEpoch }) {
  let attempt = 0;
  while (attempt < 3) {
    let response;
    try {
      response = await authFetch(`${API_BASE_URL}/sessions/progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          continueCode,
          accessCode,
          lastQuestionIndex: questionIndex,
          activityType,
          ...(viewEpoch ? { viewEpoch } : {})
        })
      });
    } catch (error) {
      attempt += 1;
      if (attempt >= 3) throw error;
      await wait(300 * (2 ** attempt));
      continue;
    }

    if (response.ok) return response.json();
    const responseText = await response.text();
    let responseBody = {};
    try { responseBody = JSON.parse(responseText); } catch { responseBody = {}; }
    attempt += 1;
    if (!RETRYABLE_PROGRESS_STATUS.has(response.status) || attempt >= 3) {
      const error = new Error(
        responseBody.error || `Failed to update session progress (${response.status}) ${responseText}`
      );
      error.status = response.status;
      error.code = responseBody.code || 'SESSION_PROGRESS_FAILED';
      throw error;
    }
    await wait(300 * (2 ** attempt));
  }
  throw new Error('Failed to update session progress');
}

function enqueueSessionProgress(payload) {
  const key = `${payload.accessCode}:${payload.continueCode}`;
  const previous = sessionProgressQueues.get(key) || Promise.resolve();
  const current = previous
    .catch(() => undefined)
    .then(() => sendSessionProgress(payload));
  sessionProgressQueues.set(key, current);
  current.finally(() => {
    if (sessionProgressQueues.get(key) === current) sessionProgressQueues.delete(key);
  }).catch(() => undefined);
  return current;
}

/* --------------------------------------------------------- */
/*  Admin-access helpers                                     */
/* --------------------------------------------------------- */

/** The current admin key (empty when not in admin mode) */
let __adminKey = '';
let __jwtToken = '';    // ✅ JWT token storage

export function acceptCookieAuthentication() {
  // The browser uses the server's HttpOnly cookie. A bearer token persisted by
  // an older release must not override a newly issued cookie on later calls.
  __jwtToken = '';
  localStorage.removeItem('jwtToken');
  authBoundaryTransitionStarted = false;
}

/**
 * ✅ JWT: Store the JWT token after login.
 */
export function setJwtToken(token = '') {
  __jwtToken = (token || '').trim();
  if (token) {
    authBoundaryTransitionStarted = false;
    localStorage.setItem('jwtToken', token);
  } else {
    localStorage.removeItem('jwtToken');
  }
}

/**
 * ✅ JWT: Get the current JWT token.
 */
export function getJwtToken() {
  if (__jwtToken) return __jwtToken;
  const stored = localStorage.getItem('jwtToken');
  if (stored) {
    __jwtToken = stored;
    return stored;
  }
  return '';
}

/**
 * ✅ JWT: Clear all auth data (for logout).
 */
export function clearAuth() {
  __jwtToken = '';
  __adminKey = '';
  localStorage.removeItem('jwtToken');
  localStorage.removeItem('accessCode');
}

export async function logout() {
  if (logoutPromise) return logoutPromise;
  authBoundaryTransitionStarted = true;
  clearAuth();
  logoutPromise = fetch(`${API_BASE_URL}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
  }).then(response => {
    if (!response.ok) throw new Error(`Server logout was not confirmed (${response.status})`);
    return response;
  }).finally(() => { logoutPromise = null; });
  return logoutPromise;
}

/**
 * ✅ JWT: Check if JWT token is expired.
 */
export function isTokenExpired() {
  const token = getJwtToken();
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

/**
 * Call once after a successful admin login.
 * All subsequent requests to protected endpoints
 * will automatically include the header required
 * by server-side `adminAuth`.
 */
export function setAdminAccessCode(code = '') {
  __adminKey = (code || '').trim();
  if (__adminKey) acceptCookieAuthentication();
}

/**
 * Builds an Authorization header only when an actual JWT is available.
 * Admin login normally uses the server's HttpOnly cookie; the administrator
 * access code is merely an in-memory mode marker and must never masquerade as
 * a bearer token.
 */
function authHeaders() {
  // Browser authentication is cookie-only. In particular, never resurrect a
  // legacy token from localStorage: the server gives Authorization precedence
  // over the cookie, so one stale token would shadow a freshly issued session.
  return {};
}

/**
 * ✅ SECURITY: Enhanced authFetch with JWT token and 401 handling
 */
async function authFetch(url, options = {}) {
  const mergedHeaders = { ...(options.headers || {}), ...authHeaders() };

  const request = {
    ...options,
    credentials: options.credentials || 'same-origin',
    headers: mergedHeaders
  };
  let response = await fetch(url, request);
  // Safe reads may retry a transient authentication check. Mutating requests
  // retain their existing outbox/idempotency rules rather than being replayed.
  if (String(options.method || 'GET').toUpperCase() === 'GET' && response.status === 503) {
    const failure = await response.clone().json().catch(() => ({}));
    if (failure.code === 'DATABASE_BUSY' || failure.code === 'AUTH_SERVICE_UNAVAILABLE') {
      await wait(Math.min(2000, Math.max(250, (Number(failure.retryAfter) || 1) * 1000)));
      response = await fetch(url, request);
    }
  }

  // A burst of failing requests may observe the same expired/revoked cookie.
  // Emit exactly one UI boundary transition until authentication succeeds.
  if (response.status === 401) {
    const data = await response.clone().json().catch(() => ({}));
    const bootstrapRequest = /\/api\/(?:users\/verify|users\/login|admin\/login)(?:\?|$)/.test(String(url));
    if (!bootstrapRequest && SESSION_BOUNDARY_CODES.has(data.code) && !authBoundaryTransitionStarted) {
      authBoundaryTransitionStarted = true;
      console.warn('🔒 [AUTH] Session boundary reached - requesting re-authentication');
      clearAuth();
      window.dispatchEvent(new CustomEvent('auth:expired', { detail: { code: data.code } }));
    }
  }

  return response;
}

async function requireInstructionImportSession() {
  const response = await authFetch(`${API_BASE_URL}/auth/session`, {
    method: 'GET',
    headers: { 'X-AssessNow-Session-Purpose': 'instructional-import' }
  });
  if (!response.ok) throw await apiResponseError(response, 'Your dashboard session must be renewed before importing lessons');
  const status = await response.json();
  if (!Number.isFinite(Number(status.remainingSeconds)) || Number(status.remainingSeconds) < 120) {
    if (!authBoundaryTransitionStarted) {
      authBoundaryTransitionStarted = true;
      clearAuth();
      window.dispatchEvent(new CustomEvent('auth:expired', { detail: { code: 'TOKEN_EXPIRING' } }));
    }
    const error = new Error('Your dashboard session is about to expire. Sign in again, then preview and publish the ZIP. No lessons were imported.');
    error.code = 'TOKEN_EXPIRING';
    error.status = 401;
    throw error;
  }
  return status;
}

async function postResponses(performanceId, responses) {
  const res = await authFetch(`${API_BASE_URL}/responses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ performanceId, responses }),
  });
  if (!res.ok) {
    const failure = await res.json().catch(() => ({}));
    const error = new Error(failure.error || 'Failed to save responses');
    error.code = failure.code;
    error.status = res.status;
    error.minimumWords = failure.minimumWords;
    error.maximumWords = failure.maximumWords;
    error.wordCount = failure.wordCount;
    throw error;
  }
  return res.json();
}

async function apiResponseError(response, fallbackMessage) {
  const payload = await response.clone().json().catch(() => ({}));
  const error = new Error(payload.error || fallbackMessage);
  error.status = response.status;
  error.code = payload.code || null;
  if (Array.isArray(payload.details)) {
    error.details = payload.details.slice(0, 10);
  }
  if (payload.currentRevision !== undefined) {
    error.currentRevision = Number(payload.currentRevision);
  }
  if (payload.activeAttemptCount !== undefined) {
    error.activeAttemptCount = Number(payload.activeAttemptCount);
  }
  return error;
}

/* --------------------------------------------------------- */
/*  Utilities                                                */
/* --------------------------------------------------------- */

/**
 * ✅ FIXED: Normalizes an assessment-type string to match database format
 */
export function normalizeAssessmentType(rawType) {
  if (!rawType) return '';
  const lower = rawType.trim().toLowerCase();

  if (lower.includes('reading')) return 'reading';
  if (/math[\s-]*calculation/.test(lower)) return 'calculation';
  if (/math[\s-]*problem[\s-]*solving/.test(lower))
    return 'problem-solving';
  if (/solar[\s-]*system[\s-]*general[\s-]*quiz/.test(lower))
    return 'solar-system-general-quiz';
  if (/phonics[\s-]*assessment/.test(lower)) return 'phonics-assessment';
  if (/spelling[\s-]*assessment/.test(lower)) return 'spelling-assessment';
  if (/vocabulary[\s-]*assessment/.test(lower))
    return 'vocabulary-assessment';
  if (/written[\s-]*language[\s-]*mechanics[\s-]*assessment/.test(lower))
    return 'written-language-mechanics-assessment';

  return lower
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

/* --------------------------------------------------------- */
/*  API                                                      */
/* --------------------------------------------------------- */

export const api = {
  lessonMonitor: {
    async get(endpoint='',params={}) {
      const controller=new AbortController(),timer=setTimeout(()=>controller.abort(),endpoint.endsWith('/live')?3000:8000);
      try {const response=await authFetch(API_BASE_URL+'/lesson-monitor'+(endpoint?'/'+endpoint:'')+'?'+new URLSearchParams(params),{signal:controller.signal});if(!response.ok)throw await apiResponseError(response,'Lesson monitor unavailable');return await response.json();}
      catch(error){if(error.name==='AbortError')throw new Error('Lesson monitor connection timed out. Refresh to confirm the current status.');throw error;}
      finally{clearTimeout(timer);}
    },
    async post(endpoint,body={}) {
      const controller=new AbortController(),timer=setTimeout(()=>controller.abort(),endpoint.endsWith('/scroll')?2000:endpoint==='heartbeat'||endpoint.endsWith('/annotations')?3000:8000);
      try {const response=await authFetch(API_BASE_URL+'/lesson-monitor/'+endpoint,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body),signal:controller.signal});if(!response.ok)throw await apiResponseError(response,'Lesson action failed');return await response.json();}
      catch(error){if(error.name==='AbortError')throw new Error('The lesson action could not be confirmed. Refresh its status before trying again.');throw error;}
      finally{clearTimeout(timer);}
    }
  },
  instructionalLessons: {
    async uploadZip(action, mainUserId, file, fields = {}) {
      // A bulk import may spend time validating hundreds of HTML files. Check
      // the authenticated boundary before any bytes are committed so an old
      // session cannot expire only after a successful publication.
      if (action === 'import') await requireInstructionImportSession();
      const body = new FormData();
      body.append('file', file);
      for (const [key, value] of Object.entries(fields)) body.append(key, typeof value === 'string' ? value : JSON.stringify(value));
      const response = await authFetch(`${API_BASE_URL}/instructional-lessons/bulk/${action}?mainUserId=${encodeURIComponent(mainUserId)}`, { method: 'POST', body });
      if (response.status === 413) throw new Error('The server rejected this ZIP size. Use a smaller lesson batch or ask the administrator to check the proxy upload limit.');
      if (!response.ok) throw await apiResponseError(response, 'Lesson ZIP import failed');
      return response.json();
    },
    async get(endpoint, params = {}) {
      const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value !== undefined && value !== null));
      const response = await authFetch(`${API_BASE_URL}/instructional-lessons/${endpoint}${query.size ? `?${query}` : ''}`);
      if (!response.ok) throw await apiResponseError(response, 'Instructional lesson unavailable');
      return response.json();
    },
    async post(endpoint, data) {
      const response = await authFetch(`${API_BASE_URL}/instructional-lessons/${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });
      if (!response.ok) throw await apiResponseError(response, 'Unable to save instructional lesson');
      return response.json();
    },
    async exportCoverage(params) {
      const response = await authFetch(`${API_BASE_URL}/instructional-lessons/coverage.txt?${new URLSearchParams(params)}`);
      if (!response.ok) throw await apiResponseError(response, 'Coverage export unavailable');
      return response.blob();
    },
    async exportSubuserOverview(params) {
      const response = await authFetch(`${API_BASE_URL}/instructional-lessons/subuser-overview.txt?${new URLSearchParams(params)}`);
      if (!response.ok) throw await apiResponseError(response, 'Subuser coverage export unavailable');
      return response.blob();
    },
    async exportUniqueObjectives(params) {
      const response = await authFetch(`${API_BASE_URL}/instructional-lessons/subuser-objectives.txt?${new URLSearchParams(params)}`);
      if (!response.ok) throw await apiResponseError(response, 'Unique objectives export unavailable');
      return response.blob();
    },
    async exportObjectiveAliases(params) {
      const response = await authFetch(`${API_BASE_URL}/instructional-lessons/objective-aliases.txt?${new URLSearchParams(params)}`);
      if (!response.ok) throw await apiResponseError(response, 'Objective alias export unavailable');
      return response.blob();
    }
  },
  getDashboardTheme: async ({ signal } = {}) => {
    const response = await authFetch(`${API_BASE_URL}/dashboard-preferences`, { signal });
    if (!response.ok) throw new Error('Unable to load dashboard appearance');
    return response.json();
  },
  saveDashboardTheme: async (theme, { signal } = {}) => {
    const response = await authFetch(`${API_BASE_URL}/dashboard-preferences`, {
      method: 'PUT', signal, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ theme })
    });
    if (!response.ok) throw new Error('Unable to save dashboard appearance');
    return response.json();
  },
  logout,
  /* ---------------- generic helper ---------------------- */
  async post(endpoint, data) {
    const res = await authFetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw await apiResponseError(
      res,
      `POST ${endpoint} failed (${res.status})`
    );
    return res.json();
  },

  /* ----------------------------------------------------- */
  /*  ✅ SECURITY: New answer validation endpoints         */
  /* ----------------------------------------------------- */
  validateAnswer: async (
    questionId,
    studentAnswer,
    assessmentFileName,
    accessCode,
    performanceId
  ) => {
    const res = await authFetch(`${API_BASE_URL}/validate-answer`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        questionId,
        studentAnswer,
        assessmentFileName,
        accessCode,
        performanceId
      })
    });

    if (!res.ok) {
      throw new Error(`Answer validation failed: ${res.status}`);
    }

    const result = await res.json();
    return result.isCorrect;
  },

  validateAnswersBatch: async (
    answers,
    assessmentFileName,
    accessCode,
    performanceId
  ) => {
    const res = await authFetch(`${API_BASE_URL}/validate-answers-batch`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        answers,
        assessmentFileName,
        accessCode,
        performanceId
      })
    });

    if (!res.ok) {
      throw new Error(`Batch validation failed: ${res.status}`);
    }

    const result = await res.json();
    return result.results;
  },

  /* ----------------------------------------------------- */
  /*  ✅ SECURITY: Secure assessment file loading          */
  /* ----------------------------------------------------- */
  getAssessmentFile: async (fileName, performanceId = null, options = {}) => {
    if (!fileName) throw new Error('Assessment filename is required');
    const resumeQuery = performanceId
      ? `?performanceId=${encodeURIComponent(performanceId)}`
      : '';
    const res = await authFetch(
      `${API_BASE_URL}/assessment-file/${encodeURIComponent(fileName)}${resumeQuery}`,
      { cache: 'no-store', signal: options.signal }
    );
    
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        throw new Error('Access denied. Please check your access code and try again.');
      }
      throw new Error(`Failed to load assessment file: ${res.statusText}`);
    }

    const data = await res.json();
    
    // Defense in depth: ordinary testing/report reads must not contain any
    // supported answer-key field anywhere in the assessment. Checking only the
    // first item missed keys used by multi-select, matching, equation, and graph
    // question types.
    const answerKeyFields = new Set([
      'answer', 'answers', 'answerkey', 'answerkeys', 'expectedanswer',
      'answerexplanation', 'answerrationale', 'expectedanswers',
      'expectedresponse', 'expectedresponses', 'acceptableanswers',
      'acceptableresponses', 'acceptedanswers', 'acceptedresponses',
      'solution', 'solutions', 'solutionsteps', 'workedsolution',
      'workedsolutions', 'solutionexplanation', 'scoringkey', 'scoringkeys',
      'markscheme', 'scoringscheme', 'responsekey', 'responsekeys',
      'keyedresponse', 'keyedanswer', 'correct', 'iscorrect', 'correctness', 'correctanswer',
      'correctanswers', 'correctresponse', 'correctresponses',
      'correctoption', 'correctoptions', 'correctchoice', 'correctchoices',
      'correctmatches', 'correctorder', 'correctplacement', 'correcthotspot',
      'correctselections', 'correctparts', 'correctpair', 'correctequation',
      'correctpoints', 'modelresponse', 'modelanswer', 'sampleanswer',
      'sampleresponse', 'idealanswer', 'idealresponse', 'referenceanswer',
      'referenceresponse', 'goldanswer', 'goldresponse', 'exemplaranswer',
      'exemplarresponse', 'teacheranswer', 'groundtruth',
      'groundtruthanswer', 'groundtruthresponse'
    ]);
    const normalizeSecurityKey = (key) => String(key ?? '')
      .normalize('NFKC')
      .toLocaleLowerCase('en-US')
      .replace(/[^a-z0-9]+/g, '');
    const isAnswerKeyField = (rawKey) => {
      const key = normalizeSecurityKey(rawKey);
      if (!key) return false;
      if (answerKeyFields.has(key)) return true;
      return /^(?:answer|answers|solution|solutions|scoring|response)(?:key|keys)(?:value|values|text|texts|data|list)?$/.test(key)
        || /^keyed(?:answer|answers|response|responses|choice|choices|option|options)(?:value|values|text|texts|data|list)?$/.test(key)
        || /^(?:is)?correct(?:answer|answers|response|responses|option|options|choice|choices|selection|selections|value|values|text|texts|key|keys|sentence|word|phrase|number|label|mapping|map)?(?:key|keys|value|values|text|texts|data|list|explanation|rationale)?$/.test(key)
        || /^(?:expected|acceptable|accepted|model|sample|ideal|reference|gold|exemplar|teacher)(?:answer|answers|response|responses|solution|solutions|option|options|choice|choices)(?:key|keys|value|values|text|texts|data|list|explanation|rationale)?$/.test(key)
        || /^groundtruth(?:answer|answers|response|responses|value|values|text|texts|data)?$/.test(key)
        || /^(?:worked)?solution(?:s|steps|value|values|text|texts|data|explanation|rationale)?$/.test(key)
        || /^(?:answer|solution|response)(?:explanation|rationale)$/.test(key);
    };
    const containsAnswerKey = (value) => {
      if (!value || typeof value !== 'object') return false;
      if (Array.isArray(value)) return value.some(containsAnswerKey);
      return Object.entries(value).some(
        ([key, entry]) => isAnswerKeyField(key) || containsAnswerKey(entry)
      );
    };
    if (containsAnswerKey(data)) {
      console.error('Security alert: answer-key data was returned to a testing client');
      throw new Error('Security error: Assessment data contains answers');
    }

    return data;
  },

  getAssessmentFileForAuthoring: async (fileName, options = {}) => {
    if (!fileName) throw new Error('Assessment filename is required');
    const res = await authFetch(
      `${API_BASE_URL}/assessment-file/${encodeURIComponent(fileName)}`,
      { cache: 'no-store', headers: { 'X-Assessment-Authoring': 'true' }, signal: options.signal }
    );
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.error || `Failed to load assessment (${res.status})`);
    }
    if (res.headers.get('X-Assessment-Authoring') !== 'true') {
      throw new Error('This account does not have authoring access to the assessment');
    }
    return res.json();
  },

  /* ----------------------------------------------------- */
  /*  1) USERS                                             */
  /* ----------------------------------------------------- */
  createUser: async (userData) => {
    const res = await authFetch(`${API_BASE_URL}/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    if (!res.ok) throw new Error('Failed to create user');
    return res.json();
  },

  getAllUsers: async () => {
    const res = await authFetch(`${API_BASE_URL}/users`);
    if (!res.ok) throw new Error('Failed to fetch users');
    return res.json();
  },

  // ✅ FIXED: Use POST instead of GET for access code verification
  verifyAccessCode: async (accessCode) => {
    const res = await authFetch(`${API_BASE_URL}/users/verify`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ accessCode })
    });
    if (!res.ok) {
      if (res.status === 429) {
        const payload = await res.json().catch(() => ({}));
        const error = new Error(payload.error || 'Too many attempts. Please wait and try again.');
        error.code = 'AUTH_RATE_LIMITED';
        throw error;
      }
      if (res.status === 401 || res.status === 404) return null;
      throw new Error('Failed to verify access code');
    }
    const user = await res.json();
    acceptCookieAuthentication();
    return user;
  },

  verifyUser: async (email, pinCode, accessCode) => {
    const res = await authFetch(`${API_BASE_URL}/users/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, pinCode, accessCode }),
    });
    if (!res.ok) {
      if (res.status === 401 || res.status === 404 || res.status === 429) return null;
      throw new Error('Failed to verify user');
    }
    const user = await res.json();
    acceptCookieAuthentication();
    return user;
  },

  deleteUser: async (id) => {
    const res = await authFetch(`${API_BASE_URL}/users/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error('Failed to delete user');
    return res.json();
  },

  // ✅ ENHANCED: Better error handling and debugging for PATCH
  patchUser: async (id, data) => {   
    const res = await authFetch(`${API_BASE_URL}/users/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('❌ [API] PATCH user failed:', err);
      throw new Error(err.error || `Failed to patch user (${res.status})`);
    }
    
    const result = await res.json();
    return result;
  },

  // ✅ ENHANCED: Better error handling and debugging for PUT
  updateUser: async (id, userData) => {   
    const req = { ...userData };
    const isSub = userData.role === 'subuser' || Boolean(
      userData.parentUserId ?? userData.parent_user_id
    );

    if (isSub) {
      delete req.maxSubUsers;
    } else if (req.maxSubUsers === undefined) {
      /* fetch current maxSubUsers for parent */
      try {
        const r = await authFetch(`${API_BASE_URL}/users/${id}`);
        if (r.ok) {
          const cur = await r.json();
          req.maxSubUsers = cur.maxSubUsers ?? cur.max_subusers ?? 5;
        }
      } catch {
        req.maxSubUsers = 5;
      }
    }

    const res = await authFetch(`${API_BASE_URL}/users/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
    
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('❌ [API] PUT user failed:', err);
      throw new Error(err.error || `Failed to update user (${res.status})`);
    }
    
    const updated = await res.json();
    if (updated && !isSub) {
      updated.maxSubUsers ??= updated.max_subusers ?? userData.maxSubUsers ?? 5;
    }
    
    return updated;
  },

  // ✅ NEW: Get single user by ID (useful for debugging)
  getUser: async (id) => {
    const res = await authFetch(`${API_BASE_URL}/users/${id}`);
    if (!res.ok) {
      if (res.status === 404) return null;
      throw new Error('Failed to fetch user');
    }
    return res.json();
  },

  /* ----------------------------------------------------- */
  /*  2) SUB-USERS                                         */
  /* ----------------------------------------------------- */
  getSubUsers: async (mainUserId) => {
    try {
      const res = await authFetch(
        `${API_BASE_URL}/subusers?mainUserId=${mainUserId}`
      );
      if (!res.ok) {
        throw new Error(`Failed to fetch subusers (${res.status})`);
      }
      return (await res.json()) || [];
    } catch (e) {
      console.error('getSubUsers error', e);
      throw e;
    }
  },

  createSubUsers: async (data) => {
    const users = await api.getAllUsers();
    const main = users.find((u) => u.id === data.mainUserId);
    if (!main) throw new Error('Main user not found');

    const max = main.maxSubUsers ?? main.max_subusers ?? 5;
    const current = (await api.getSubUsers(data.mainUserId)).length;
    if (current + data.count > max)
      throw new Error(
        `Cannot create ${data.count} new sub-users (limit ${max}, ${current} exist)`
      );

    const res = await authFetch(`${API_BASE_URL}/subusers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mainUserId: data.mainUserId,
        count: data.count,
        useDifferentEmails: true,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to create sub-users');
    }
    const out = await res.json();
    return out.createdSubUsers ?? out;
  },

  updateSubUserStatus: async (id, isEnabled) => {
    const res = await authFetch(`${API_BASE_URL}/subusers/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isEnabled }),
    });
    if (!res.ok) throw new Error('Failed to update sub-user status');
    return res.json();
  },

  deleteSubUser: async (id) => {
    const res = await authFetch(`${API_BASE_URL}/subusers/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error('Failed to delete sub-user');
    return res.json();
  },

  /* ----------------------------------------------------- */
  /*  3) PERFORMANCES                                      */
  /* ----------------------------------------------------- */
  // ✅ FIXED: createPerformance function in client.js
// Replace the existing function (around line 430) with this corrected version:

createPerformance: async (performanceData) => {
  try {
    const payload = {
      ...performanceData,
      continueCode:
        performanceData.continue_code || performanceData.continueCode,
    };
    const assessmentId = assessmentIdOf(payload.assessmentId || payload.assessmentType);
    if (!assessmentId) throw new Error('assessmentId is required to create a performance');

    // The server re-reads TTS, timer, kind and pool from the registered
    // assessment/assignment. These values remain for compatibility and admin
    // diagnostics, but they are never used as an identity join key.
    const finalPayload = {
      ...payload,
      assessmentId,
      assessmentType: assessmentId,
      ...(payload.tts_enabled !== undefined && {
        tts_enabled: isEnabledFlag(payload.tts_enabled) ? 1 : 0
      }),
      ...(payload.timer_seconds !== undefined && {
        timer_seconds: Math.max(0, Math.min(300, Number.parseInt(payload.timer_seconds, 10) || 0))
      }),
      ...(payload.overall_time_limit_seconds !== undefined && {
        overall_time_limit_seconds: boundedOverallTimeSeconds(payload.overall_time_limit_seconds)
      })
    };
    const res = await authFetch(`${API_BASE_URL}/performances`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(finalPayload),
    });
    
    if (!res.ok) {
      const errorText = await res.text();
      console.error('❌ [API] Server error response:', errorText);
      
      let errorObj;
      try {
        errorObj = JSON.parse(errorText);
      } catch {
        errorObj = { error: errorText };
      }
      
      const error = new Error(errorObj.error || `HTTP ${res.status}: ${errorText}`);
      error.status = res.status;
      error.code = errorObj.code || null;
      error.details = errorObj.details || null;
      throw error;
    }
    
    return res.json();
  } catch (e) {
    console.error('❌ [API] createPerformance error:', e);
    throw e;
  }
},

// ✅ UPDATED: Enhanced getPerformances function with timer_seconds verification
// Replace the existing getPerformances function in client.js (around line 500)

getPerformances: async (filters = {}) => {
  try {
    const p = new URLSearchParams();
    if (filters.codeUsed) p.append('codeUsed', filters.codeUsed);
    if (filters.fromDate) p.append('fromDate', filters.fromDate);
    if (filters.toDate) p.append('toDate', filters.toDate);
    if (filters.includeArchived) p.append('includeArchived', filters.includeArchived);
    if (filters.assessmentId) p.append('assessmentId', filters.assessmentId);
    if (filters.completedOnly) p.append('completedOnly', 'true');
    
    // Add cache busting parameter if provided
    if (filters._cache_bust) p.append('_cache_bust', filters._cache_bust);

    const opts = filters.signal ? { signal: filters.signal } : undefined;
    const res = await authFetch(`${API_BASE_URL}/performances?${p}`, opts);
    
    if (!res.ok) throw new Error('Failed to fetch performances');
    
    const performances = await res.json();
    
    // ✅ VERIFICATION: Log first performance to check timer_seconds
    if (performances.length > 0) {
      const sample = performances[0];
      
      // ✅ CRITICAL CHECK: Verify timer_seconds exists
      if (sample.timer_seconds === undefined) {
        console.error('❌ [CLIENT] timer_seconds field is missing from API response!');
        console.error('❌ [CLIENT] Available fields:', Object.keys(sample));
        console.error('❌ [CLIENT] This means the backend API is not including timer_seconds in the SELECT query');
      } else {
      }
    }
    
    return performances;
  } catch (error) {
    console.error('❌ [CLIENT] Error fetching performances:', error);
    throw error;
  }
},

// ✅ NEW: Add missing functions to support timer debugging

// Get single performance by ID with timer verification
getPerformanceById: async (id) => {
  try {
    const res = await authFetch(`${API_BASE_URL}/performances/${id}`);
    
    if (!res.ok) {
      if (res.status === 404) return null;
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    const performance = await res.json();
    
    // ✅ VERIFICATION: Check timer field
    
    return performance;
  } catch (error) {
    console.error('❌ [CLIENT] Error fetching performance by ID:', error);
    throw error;
  }
},

getPerformanceTimeStatus: async (id) => {
  const res = await authFetch(`${API_BASE_URL}/performances/${id}/time-status`, {
    cache: 'no-store'
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.error || `Failed to load overall time status (${res.status})`);
  }
  return res.json();
},

expirePerformance: async (id, finalResponse = null) => {
  const res = await authFetch(`${API_BASE_URL}/performances/${id}/expire`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...(finalResponse ? { finalResponse } : {})
    })
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    const exception = new Error(error.error || `Failed to finalize expired assessment (${res.status})`);
    exception.status = res.status;
    exception.timeStatus = error;
    throw exception;
  }
  return res.json();
},

// ✅ NEW: Debug function to test timer data flow end-to-end
debugTimerData: async (accessCode) => {
  try {
   
    // Test the debug endpoint
    const response = await authFetch(`${API_BASE_URL}/debug/timer-data/${accessCode}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const debugData = await response.json();
    
    return debugData;
  } catch (error) {
    console.error('❌ [CLIENT] Error fetching timer debug data:', error);
    throw error;
  }
},

// ✅ NEW: Test complete timer flow from settings to performance
testTimerFlow: async (accessCode, assessmentType) => {
  try {
   
    // 1. Get timer setting
    const timerSeconds = await api.getTimerSetting(accessCode, assessmentType);
    
    // 2. Test creating a performance with timer
    const testPerformance = {
      studentName: 'Timer Test Student',
      assessmentType: assessmentType,
      gradeLevel: 'Test Grade',
      actualGradeLevel: 'Test Grade', 
      gradePerformance: '0%',
      code_used: accessCode,
      timer_seconds: timerSeconds,
      assessmentKind: 'regular',
      assessmentPool: 'NonPooled'
    };
    
    const created = await api.createPerformance(testPerformance);
    
    // 3. Verify the performance was saved with correct timer
    const saved = await api.getPerformanceById(created.id);
    
    // 4. Clean up test performance
    try {
      await api.deletePerformance(created.id);
    } catch (cleanupError) {
      console.warn('⚠️ [CLIENT] Could not clean up test performance:', cleanupError);
    }
    
    return {
      timerSetting: timerSeconds,
      performanceCreated: created.id,
      performanceSaved: saved?.timer_seconds,
      success: saved?.timer_seconds === timerSeconds,
      message: saved?.timer_seconds === timerSeconds 
        ? 'Timer flow working correctly!' 
        : `Timer mismatch: expected ${timerSeconds}, got ${saved?.timer_seconds}`
    };
    
  } catch (error) {
    console.error('❌ [CLIENT] Timer flow test failed:', error);
    throw error;
  }
},

// ✅ ENHANCED: Add timer verification to existing deletePerformance
deletePerformance: async (id) => {
  try {
    
    // Optional: Log timer data before deletion for debugging
    try {
      const performance = await api.getPerformanceById(id);
      if (performance?.timer_seconds !== undefined) {
      }
    } catch (debugError) {
      // Non-critical, continue with deletion
    }
    
    const res = await authFetch(`${API_BASE_URL}/performances/${id}`, { 
      method: 'DELETE' 
    });
    
    if (!res.ok) {
      throw new Error(`Failed to delete performance: ${res.status}`);
    }
    
    return res;
  } catch (error) {
    console.error('❌ [CLIENT] Error deleting performance:', error);
    throw error;
  }
},

previewPerformanceRecordLifecycle: async ({ performanceIds, action }) => {
  const res = await authFetch(`${API_BASE_URL}/performance-record-lifecycle/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ performanceIds, action })
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || `Unable to preview record operation (${res.status})`);
  return payload;
},

applyPerformanceRecordLifecycle: async ({ performanceIds, action, reason, confirmationToken }) => {
  const res = await authFetch(`${API_BASE_URL}/performance-record-lifecycle/apply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ performanceIds, action, reason, confirmationToken })
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || `Record operation failed (${res.status})`);
  return payload;
},

  // Attempt conditions (TTS and timing policy) are immutable snapshots.
  updatePerformance: async (id, data) => {
    try {
      const allowedFields = [
        'student_name',
        'grade_level',
        'actual_grade_level', 
        'isComplete',
        'code_used',
        'expected_record_revision'
      ];
      
      // Build clean payload with only allowed fields
      const cleanPayload = {};
      allowedFields.forEach(field => {
        if (data.hasOwnProperty(field)) {
          cleanPayload[field] = data[field];
        }
      });
            
      // Handle boolean conversions
      if (cleanPayload.isComplete !== undefined) {
        cleanPayload.isComplete = isEnabledFlag(cleanPayload.isComplete) ? 1 : 0;
      }

      // Ensure we have something to update
      if (Object.keys(cleanPayload).length === 0) {
        throw new Error('No valid fields to update');
      }
            
      const res = await authFetch(`${API_BASE_URL}/performances/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanPayload),
      });
      
      if (!res.ok) {
        const failure = await res.json().catch(() => ({}));
        throw new Error(failure.error || `Failed to update performance (${res.status})`);
      }
      
      return res.json();
    } catch (error) {
      console.error('❌ [SAFE] updatePerformance error:', error);
      throw error;
    }
  },

  /* ----------------------------------------------------- */
  /*  4) RESPONSES                                         */
  /* ----------------------------------------------------- */
  saveResponses: async (performanceId, responses) => {
    // Finish any older draft/timer writes for these items before the response
    // transaction deletes its draft. This prevents a late autosave from
    // recreating a stale draft after a successful submission.
    await Promise.all(Object.keys(responses || {}).map(questionNumber => (
      assessmentItemStateQueues.get(`${performanceId}:${questionNumber}`)?.catch(() => undefined)
      || Promise.resolve()
    )));
    let protectedIds = [];
    try {
      protectedIds = await queueProtectedResponses(performanceId, responses);
    } catch (protectionError) {
      console.warn('Protected response outbox is unavailable:', protectionError);
    }
    publishResponseSyncStatus({ state: 'saving', performanceId: Number(performanceId), pending: protectedIds.length });
    try {
      const result = await postResponses(performanceId, responses);
      await acknowledgeProtectedResponses(protectedIds);
      publishResponseSyncStatus({ state: 'saved', performanceId: Number(performanceId), pending: 0 });
      return result;
    } catch (error) {
      const retryable = !error.status || [408, 425, 429, 500, 502, 503, 504].includes(error.status);
      publishResponseSyncStatus({
        state: retryable && protectedIds.length ? 'protected' : 'error',
        performanceId: Number(performanceId),
        pending: protectedIds.length,
        message: retryable && protectedIds.length
          ? 'Connection interrupted. This answer is protected on this device.'
          : error.message
      });
      throw error;
    }
  },

  flushResponseOutbox: async (performanceId = null) => {
    const entries = await listProtectedResponses(performanceId);
    if (!entries.length) {
      if (performanceId != null) publishResponseSyncStatus({ state: 'ready', performanceId: Number(performanceId), pending: 0 });
      return { saved: 0, pending: 0 };
    }
    publishResponseSyncStatus({ state: 'reconnecting', performanceId: performanceId == null ? null : Number(performanceId), pending: entries.length });
    let saved = 0;
    for (const entry of entries) {
      try {
        const payload = await readProtectedResponse(entry);
        await postResponses(entry.performanceId, { [entry.questionNumber]: payload.response });
        await acknowledgeProtectedResponses([entry.id]);
        saved += 1;
      } catch (error) {
        await markProtectedResponseAttempt(entry).catch(() => undefined);
        publishResponseSyncStatus({
          state: !error.status || [408, 425, 429, 500, 502, 503, 504].includes(error.status) ? 'protected' : 'error',
          performanceId: entry.performanceId,
          pending: entries.length - saved,
          message: error.message
        });
        return { saved, pending: entries.length - saved, error };
      }
    }
    publishResponseSyncStatus({ state: 'saved', performanceId: performanceId == null ? null : Number(performanceId), pending: 0 });
    return { saved, pending: 0 };
  },

  clearResponseOutbox: async performanceId => clearProtectedResponses(performanceId),

  getResponses: async (performanceId, options = {}) => {
    const res = await authFetch(`${API_BASE_URL}/responses/${performanceId}`, {
      signal: options.signal
    });
    if (!res.ok) throw new Error('Failed to fetch responses');
    return res.json();
  },

  getPerformanceResponsesBatch: async (performanceIds, options = {}) => {
    const res = await authFetch(`${API_BASE_URL}/performance-responses-batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ performanceIds, strictCompleted: options.strictCompleted === true }),
      signal: options.signal
    });
    if (!res.ok) throw await apiResponseError(res, 'Unable to load performance statistics responses');
    return res.json();
  },

  getLearnerInstructionalProfile: async (learnerAccountId, options = {}) => {
    const id = Number(learnerAccountId);
    if (!Number.isSafeInteger(id) || id <= 0) {
      throw new Error('A stable learner account is required to load the instructional profile');
    }
    const res = await authFetch(
      `${API_BASE_URL}/learner-instructional-profiles/${encodeURIComponent(id)}`,
      { signal: options.signal }
    );
    if (!res.ok) throw await apiResponseError(res, 'Unable to load the unified learner instructional profile');
    return res.json();
  },

  getCaseloadInstructionalCommandCenter: async (options = {}) => {
    const params = new URLSearchParams();
    if (options.ownerId !== undefined && options.ownerId !== null && options.ownerId !== '') {
      const ownerId = Number(options.ownerId);
      if (!Number.isSafeInteger(ownerId) || ownerId <= 0) {
        throw new Error('A valid educator account is required to load this caseload');
      }
      params.set('ownerId', String(ownerId));
    }
    const suffix = params.size ? `?${params.toString()}` : '';
    const res = await authFetch(
      `${API_BASE_URL}/caseload-instructional-command-center${suffix}`,
      { signal: options.signal }
    );
    if (!res.ok) throw await apiResponseError(res, 'Unable to load the Caseload Instructional Command Center');
    return res.json();
  },

  getInstructionalWeeklyPlanner: async ({ ownerId, weekStart, signal } = {}) => {
    const owner = Number(ownerId);
    if (!Number.isSafeInteger(owner) || owner <= 0 || !/^\d{4}-\d{2}-\d{2}$/.test(String(weekStart || ''))) {
      throw new Error('A valid educator account and Monday week are required');
    }
    const params = new URLSearchParams({ ownerId: String(owner), weekStart: String(weekStart) });
    const res = await authFetch(`${API_BASE_URL}/instructional-weekly-planners?${params}`, { signal });
    if (!res.ok) throw await apiResponseError(res, 'Unable to load the instructional weekly planner');
    return res.json();
  },

  saveInstructionalWeeklyPlanner: async (weekStart, ownerId, planner, expectedRevision, options = {}) => {
    const owner = Number(ownerId);
    const revision = Number(expectedRevision);
    if (!Number.isSafeInteger(owner) || owner <= 0 || !Number.isSafeInteger(revision) || revision < 0
      || !/^\d{4}-\d{2}-\d{2}$/.test(String(weekStart || ''))) {
      throw new Error('A valid educator account, Monday week, and current planner revision are required');
    }
    const res = await authFetch(`${API_BASE_URL}/instructional-weekly-planners/${encodeURIComponent(weekStart)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ownerId: owner, planner, expectedRevision: revision }),
      signal: options.signal
    });
    if (!res.ok) throw await apiResponseError(res, 'Unable to save the instructional weekly planner');
    return res.json();
  },

  recordInstructionalWeeklyPlannerDelivery: async (
    weekStart, sessionId, ownerId, expectedRevision, delivery, options = {}
  ) => {
    const owner = Number(ownerId);
    const revision = Number(expectedRevision);
    if (!Number.isSafeInteger(owner) || owner <= 0 || !Number.isSafeInteger(revision) || revision < 1
      || !/^\d{4}-\d{2}-\d{2}$/.test(String(weekStart || '')) || !String(sessionId || '').trim()) {
      throw new Error('A saved planner session and current revision are required to record delivery');
    }
    const res = await authFetch(
      `${API_BASE_URL}/instructional-weekly-planners/${encodeURIComponent(weekStart)}/sessions/${encodeURIComponent(sessionId)}/delivery`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerId: owner, expectedRevision: revision, delivery }),
        signal: options.signal
      }
    );
    if (!res.ok) throw await apiResponseError(res, 'Unable to record weekly instructional delivery');
    return res.json();
  },

  saveLearnerInstructionalPlan: async (learnerAccountId, plan, expectedRevision, options = {}) => {
    const id = Number(learnerAccountId);
    const revision = Number(expectedRevision);
    if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(revision) || revision < 0) {
      throw new Error('A stable learner account and current plan revision are required');
    }
    const res = await authFetch(
      `${API_BASE_URL}/learner-instructional-profiles/${encodeURIComponent(id)}/plan`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan, expectedRevision: revision }),
        signal: options.signal
      }
    );
    if (!res.ok) throw await apiResponseError(res, 'Unable to save the learner instructional plan');
    return res.json();
  },

  saveInstructionalActionCenter: async (learnerAccountId, workspace, expectedRevision, options = {}) => {
    const id = Number(learnerAccountId);
    const revision = Number(expectedRevision);
    if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(revision) || revision < 0) {
      throw new Error('A stable learner account and current action-center revision are required');
    }
    const res = await authFetch(
      `${API_BASE_URL}/learner-instructional-profiles/${encodeURIComponent(id)}/action-center`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace, expectedRevision: revision }),
        signal: options.signal
      }
    );
    if (!res.ok) throw await apiResponseError(res, 'Unable to save the Instructional Action Center');
    return res.json();
  },

  getPerformanceWritingProfiles: async (performanceIds) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const res = await authFetch(`${API_BASE_URL}/performance-writing-profiles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ performanceIds }),
        signal: controller.signal
      });
      if (!res.ok) throw await apiResponseError(res, 'Unable to load writing profiles');
      return await res.json();
    } finally {
      clearTimeout(timeout);
    }
  },

  saveAssessmentItemState: async (performanceId, itemState, accessCodeOverride = '') => {
    const accessCode = accessCodeOverride || localStorage.getItem('accessCode') || '';
    const queueKey = `${performanceId}:${itemState?.questionNumber}`;
    const previous = assessmentItemStateQueues.get(queueKey) || Promise.resolve();
    const pending = previous.catch(() => undefined).then(async () => {
      const res = await authFetch(`${API_BASE_URL}/performances/${performanceId}/item-state`, {
        method: 'PUT',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(itemState),
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to save item progress');
      }
      return res.json();
    });
    assessmentItemStateQueues.set(queueKey, pending);
    try {
      return await pending;
    } finally {
      if (assessmentItemStateQueues.get(queueKey) === pending) {
        assessmentItemStateQueues.delete(queueKey);
      }
    }
  },

  /* ----------------------------------------------------- */
  /*  5) ASSESSMENT SETTINGS - ✅ ENHANCED WITH TIMER     */
  /* ----------------------------------------------------- */
  createAssessmentSetting: async (data) => {
    const payload = {
      ...data,
      ...(data.tts_enabled !== undefined && {
        tts_enabled: isEnabledFlag(data.tts_enabled) ? 1 : 0,
      }),
      ...(data.is_visible !== undefined && {
        is_visible: isEnabledFlag(data.is_visible) ? 1 : 0,
      }),
      ...(data.is_assigned !== undefined && {
        is_assigned: isEnabledFlag(data.is_assigned) ? 1 : 0,
      }),
      // ✅ NEW: Add timer support
      ...(data.min_question_time !== undefined && {
        min_question_time: parseInt(data.min_question_time) || 0,
      }),
      ...(data.timer_mode !== undefined && {
        timer_mode: normalizeTimerMode(data.timer_mode, data.min_question_time),
      }),
      ...(data.overall_time_limit_seconds !== undefined && {
        overall_time_limit_seconds: boundedOverallTimeSeconds(data.overall_time_limit_seconds),
      }),
    };
    const res = await authFetch(`${API_BASE_URL}/assessment-settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to create assessment setting');
    }
    return res.json();
  },

  getAllAssessmentSettings: async () => {
    // ✅ SECURITY: Only admins can use GET, others should use getAssessmentSettings
    if (!__adminKey) {
      throw new Error('getAllAssessmentSettings is admin-only. Use getAssessmentSettings instead.');
    }
    
    const res = await authFetch(`${API_BASE_URL}/assessment-settings`);
    if (!res.ok) throw new Error('Failed to fetch assessment settings');
    return res.json();
  },

  /* ----------------------------------------------------- */
  /*  ✅ FIXED: GET assessment-settings with proper method */
  /* ----------------------------------------------------- */
  getAssessmentSettings: async (opts = {}) => {
    const requestedAccessCode = String(opts.accessCode || '').trim();

    /* Admin-wide dashboard load. A supplied account code must remain scoped;
       otherwise a sub-user dialog downloads the entire settings table. */
    if (__adminKey && !requestedAccessCode) {
      const res = await authFetch(`${API_BASE_URL}/assessment-settings`);
      if (!res.ok) throw new Error('Failed to fetch assessment settings');
      return res.json();
    }

    /* Teacher, sub-user, or explicitly scoped administrator request. */
    const accessCode =
      requestedAccessCode || localStorage.getItem('accessCode') || '';

    if (!accessCode) {
      throw new Error('Teacher request needs accessCode');
    }

    const res = await authFetch(`${API_BASE_URL}/assessment-settings/teacher`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ accessCode })
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.error || `Failed to fetch assessment settings (${res.status})`);
    }
    return res.json();
  },

  // ✅ SECURITY: New function for getting available assessments
  getItemAnalysisCatalog: async (accessCodes, options = {}) => {
    const res = await authFetch(`${API_BASE_URL}/item-analysis/catalog`, {
      method: 'POST', cache: 'no-store', signal: options.signal,
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ accessCodes })
    });
    if (!res.ok) throw new Error('Unable to load historical analysis assessments');
    return res.json();
  },

  getItemAnalysisDefinitions: async (performanceIds, options = {}) => {
    const res = await authFetch(`${API_BASE_URL}/item-analysis/definitions`, {
      method: 'POST', cache: 'no-store', signal: options.signal,
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ performanceIds })
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.error || 'Unable to load historical definitions');
    }
    return res.json();
  },

  getAvailableAssessments: async (accessCode) => {
    if (!accessCode) {
      throw new Error('Access code is required');
    }
    
    const res = await authFetch(`${API_BASE_URL}/assessment-settings/available`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ accessCode })
    });
    
    if (!res.ok) {
      if (res.status === 404) return [];
      throw new Error('Failed to fetch available assessments');
    }
    return res.json();
  },

  getResumableSessions: async () => {
    const res = await authFetch(`${API_BASE_URL}/sessions/resumable`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!res.ok) {
      const payload = await res.json().catch(() => ({}));
      throw new Error(payload.error || 'Failed to load resumable assessments');
    }
    const payload = await res.json();
    return Array.isArray(payload?.sessions) ? payload.sessions : [];
  },

  // ✅ ENHANCED: updateAssessmentSetting with timer support
  updateAssessmentSetting: async (id, data) => {
    const payload = {
      ...data,
      ...(data.tts_enabled !== undefined && {
        tts_enabled: isEnabledFlag(data.tts_enabled) ? 1 : 0,
      }),
      ...(data.is_visible !== undefined && {
        is_visible: isEnabledFlag(data.is_visible) ? 1 : 0,
      }),
      ...(data.is_assigned !== undefined && {
        is_assigned: isEnabledFlag(data.is_assigned) ? 1 : 0,
      }),
      // ✅ NEW: Add timer support with validation
      ...(data.min_question_time !== undefined && {
        min_question_time: Math.max(0, Math.min(300, parseInt(data.min_question_time) || 0)),
      }),
      ...(data.timer_mode !== undefined && {
        timer_mode: normalizeTimerMode(data.timer_mode, data.min_question_time),
      }),
      ...(data.overall_time_limit_seconds !== undefined && {
        overall_time_limit_seconds: boundedOverallTimeSeconds(data.overall_time_limit_seconds),
      }),
    };
        
    const res = await authFetch(`${API_BASE_URL}/assessment-settings/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('❌ [TIMER] Update failed:', err);
      throw new Error(err.error || 'Failed to update assessment setting');
    }
    const result = await res.json();
    return result;
  },

  deleteAssessmentSetting: async (id, expectedRevision) => {
    const revision = Number(expectedRevision);
    const res = await authFetch(`${API_BASE_URL}/assessment-settings/${id}?expectedRevision=${encodeURIComponent(revision)}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error('Failed to delete assessment setting');
    return res.json();
  },

  // ✅ NEW: Get timer setting for specific assessment
  getTimerSetting: async (accessCode, assessmentType) => {
    try {
      const assessmentId = assessmentIdOf(assessmentType);
      if (!accessCode || !assessmentId) return 0;
      const res = await authFetch(
        `${API_BASE_URL}/assessment-settings/timer/${encodeURIComponent(assessmentId)}?accessCode=${encodeURIComponent(accessCode)}`
      );
      if (!res.ok) return 0;
      const setting = await res.json();
      return setting.timerSeconds || 0;
    } catch (error) {
      console.warn('Failed to get timer setting:', error);
      return 0; // Default to no timer
    }
  },

  // ✅ NEW: Batch update assessment settings (useful for timer bulk operations)
  batchUpdateAssessmentSettings: async (updates) => {
    const results = [];
    for (const update of updates) {
      try {
        const result = await api.updateAssessmentSetting(update.id, update.data);
        results.push({ success: true, id: update.id, result });
      } catch (error) {
        results.push({ success: false, id: update.id, error: error.message });
      }
    }
    return results;
  },

  /* ----------------------------------------------------- */
  /*  6) ASSESSMENTS                                       */
  /* ----------------------------------------------------- */
  uploadAssessmentFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await authFetch(`${API_BASE_URL}/upload`, {
      method: 'POST',
      body: formData,
    });
    if (!res.ok) {
      throw await apiResponseError(res, `Failed to upload assessment (${res.status})`);
    }
    return res.json();
  },

  saveAssessmentFile: async ({ fileName, content, assessmentId, expectedRevision }) => {
    const res = await authFetch(`${API_BASE_URL}/save-assessment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName, content, assessmentId, expectedRevision }),
    });
    if (!res.ok) {
      throw await apiResponseError(res, `Failed to save assessment (${res.status})`);
    }
    return res.json();
  },

  listAssessmentFiles: async () => {
    const res = await authFetch(`${API_BASE_URL}/list-files`);
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.error || `Failed to list assessment files (${res.status})`);
    }
    const data = await res.json();
    return Array.isArray(data.files) ? data.files : [];
  },

  createAssessment: async (data) => {
    const payload = {
      ...data,
      assessmentPool:
        data.assessmentKind === 'regular'
          ? data.assessmentPool || 'NonPooled'
          : 'NA'
    };
    const res = await authFetch(`${API_BASE_URL}/assessments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      throw await apiResponseError(res, 'Failed to create assessment');
    }
    return res.json();
  },

  getAllAssessments: async (options = {}) => {
    const res = await authFetch(`${API_BASE_URL}/assessments`, { signal: options.signal });
    if (!res.ok) throw new Error('Failed to fetch assessments');
    return res.json();
  },

  runAssessmentIntegrityAudit: async ({ force = false, signal } = {}) => {
    const res = await authFetch(`${API_BASE_URL}/admin/assessment-integrity-audit/run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ force }),
      cache: 'no-store',
      signal
    });
    if (!res.ok) throw await apiResponseError(res, `Assessment audit failed (${res.status})`);
    return res.json();
  },

  downloadAssessmentRepairBundle: async ({ signal } = {}) => {
    const res = await authFetch(`${API_BASE_URL}/admin/assessment-integrity-audit/export-repair-bundle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
      cache: 'no-store',
      signal
    });
    if (!res.ok) throw await apiResponseError(res, `Assessment repair export failed (${res.status})`);
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const fileName = (match?.[1] || 'assessnow-assessment-repair-request.json')
      .replace(/[^a-zA-Z0-9._-]/g, '_');
    return { blob: await res.blob(), fileName };
  },

  fixAssessmentIntegrity: async (assessmentId, { expectedChecksum, expectedRevision, preview = true, targetPercentage }) => {
    const res = await authFetch(
      `${API_BASE_URL}/admin/assessment-integrity-audit/${encodeURIComponent(assessmentId)}/fix`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expectedChecksum,
          expectedRevision,
          preview,
          ...(targetPercentage == null ? {} : { targetPercentage })
        }),
        cache: 'no-store'
      }
    );
    if (!res.ok) throw await apiResponseError(res, `Assessment fix failed (${res.status})`);
    return res.json();
  },

  getAssessment: async (id) => {
    const res = await authFetch(`${API_BASE_URL}/assessments/${id}`);
    if (!res.ok) {
      if (res.status === 404) return null;
      throw new Error('Failed to fetch assessment');
    }
    return res.json();
  },

  updateAssessment: async (id, data) => {
    const payload = {
      ...data,
      assessmentPool:
        data.assessmentKind === 'regular'
          ? data.assessmentPool || 'NonPooled'
          : 'NA'
    };
    const res = await authFetch(`${API_BASE_URL}/assessments/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to update assessment');
    }
    return res.json();
  },

  updateAssessmentDisplayProfile: async (id, displayProfileMode, expectedRevision) => {
    const res = await authFetch(
      `${API_BASE_URL}/admin/assessments/${encodeURIComponent(id)}/display-profile`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayProfileMode, expectedRevision }),
        cache: 'no-store'
      }
    );
    if (!res.ok) throw await apiResponseError(res, `Display profile update failed (${res.status})`);
    return res.json();
  },

  getAssessmentDisplayDefault: async () => {
    const res = await authFetch(`${API_BASE_URL}/admin/assessment-display-default`, { cache: 'no-store' });
    if (!res.ok) throw await apiResponseError(res, `Master display default lookup failed (${res.status})`);
    return res.json();
  },

  updateAssessmentDisplayDefault: async (displayProfile, expectedRevision) => {
    const res = await authFetch(`${API_BASE_URL}/admin/assessment-display-default`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ displayProfile, expectedRevision }),
      cache: 'no-store'
    });
    if (!res.ok) throw await apiResponseError(res, `Master display default update failed (${res.status})`);
    return res.json();
  },

  getItemBankSummary: async ({
    jobLimit = 25,
    jobOffset = 0,
    sourceLimit = 25,
    sourceOffset = 0,
    signal
  } = {}) => {
    const params = new URLSearchParams({
      jobLimit: String(jobLimit),
      jobOffset: String(jobOffset),
      sourceLimit: String(sourceLimit),
      sourceOffset: String(sourceOffset)
    });
    const res = await authFetch(`${API_BASE_URL}/item-bank/summary?${params.toString()}`, {
      cache: 'no-store',
      signal
    });
    if (!res.ok) {
      throw await apiResponseError(res, 'Failed to load the Master Item Bank');
    }
    return res.json();
  },

  getItemBankOnboardingStatus: async ({ signal } = {}) => {
    const res = await authFetch(`${API_BASE_URL}/item-bank/onboarding/status`, {
      cache: 'no-store',
      signal
    });
    if (!res.ok) {
      throw await apiResponseError(res, 'Failed to load assessment indexing status');
    }
    return res.json();
  },

  getFeatureCapabilities: async () => {
    const res = await authFetch(`${API_BASE_URL}/feature-capabilities`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to load feature capabilities');
    }
    return res.json();
  },

  getInstructionalPriorities: async ({ accessCode, signal } = {}) => {
    const query = accessCode ? `?accessCode=${encodeURIComponent(accessCode)}` : '';
    const res = await authFetch(`${API_BASE_URL}/instructional-priorities${query}`, {
      cache: 'no-store',
      signal
    });
    if (!res.ok) throw await apiResponseError(res, 'Failed to load instructional priorities');
    return res.json();
  },

  getRecommendationStrategy: async ({ accessCode, system = false, signal } = {}) => {
    const query = system
      ? '?scope=system'
      : `?accessCode=${encodeURIComponent(accessCode || '')}`;
    const res = await authFetch(`${API_BASE_URL}/recommendation-strategy${query}`, {
      cache: 'no-store',
      signal
    });
    if (!res.ok) throw await apiResponseError(res, 'Failed to load recommendation strategy');
    return res.json();
  },

  updateRecommendationStrategy: async ({ scope = 'account', accessCode, mode, expectedRevision }) => {
    const res = await authFetch(`${API_BASE_URL}/recommendation-strategy`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ scope, accessCode, mode, expectedRevision })
    });
    if (!res.ok) throw await apiResponseError(res, 'Failed to update recommendation strategy');
    return res.json();
  },

  getAssessmentSequencePolicy: async ({ accessCode, system = false, signal } = {}) => {
    const query = system
      ? '?scope=system'
      : `?accessCode=${encodeURIComponent(accessCode || '')}`;
    const res = await authFetch(`${API_BASE_URL}/assessment-sequence-policy${query}`, {
      cache: 'no-store',
      signal
    });
    if (!res.ok) throw await apiResponseError(res, 'Failed to load assessment sequence policy');
    return res.json();
  },

  updateAssessmentSequencePolicy: async ({
    scope = 'account', accessCode, mode = 'custom', dailyLimit,
    breakMinutes, allowTakeAnother, expectedRevision
  }) => {
    const res = await authFetch(`${API_BASE_URL}/assessment-sequence-policy`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        scope, accessCode, mode, dailyLimit, breakMinutes,
        allowTakeAnother, expectedRevision
      })
    });
    if (!res.ok) throw await apiResponseError(res, 'Failed to update assessment sequence policy');
    return res.json();
  },

  getStudentAssessmentSequenceStatus: async ({ learnerAlias, actualGradeLevel, signal } = {}) => {
    const res = await authFetch(`${API_BASE_URL}/student-assessment-sequence/status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ learnerAlias, actualGradeLevel }),
      cache: 'no-store',
      signal
    });
    if (!res.ok) throw await apiResponseError(res, 'Failed to load today’s assessment sequence');
    return res.json();
  },

  getAdminFeatureSettings: async ({ signal } = {}) => {
    const res = await authFetch(`${API_BASE_URL}/admin/feature-settings`, {
      cache: 'no-store',
      signal
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to load administrator feature settings');
    }
    return res.json();
  },

  getOperationalHealth: async ({ signal } = {}) => {
    const res = await authFetch(`${API_BASE_URL}/admin/operational-health`, {
      cache: 'no-store',
      signal
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to load operational health');
    }
    return res.json();
  },

  downloadOperationalHealthReport: async ({ signal } = {}) => {
    const res = await authFetch(`${API_BASE_URL}/admin/operational-health/export`, {
      cache: 'no-store',
      signal
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to export operational health');
    }
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const fileName = (match?.[1] || 'assessnow-operational-health.json')
      .replace(/[^a-zA-Z0-9._-]/g, '_');
    return { blob: await res.blob(), fileName };
  },

  updateAdminFeatureSettings: async settings => {
    const res = await authFetch(`${API_BASE_URL}/admin/feature-settings`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to update administrator feature settings');
    }
    return res.json();
  },

  getItemBankFilters: async ({ signal } = {}) => {
    const res = await authFetch(`${API_BASE_URL}/item-bank/filters`, {
      cache: 'no-store',
      signal
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to load item-bank filters');
    }
    return res.json();
  },

  getBankAssessmentTemplates: async ({ signal } = {}) => {
    const res = await authFetch(`${API_BASE_URL}/item-bank/templates`, {
      cache: 'no-store',
      signal
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to load native bank assessment templates');
    }
    return res.json();
  },

  updateBankAssessmentTemplate: async (templateId, blueprint) => {
    const res = await authFetch(
      `${API_BASE_URL}/item-bank/templates/${encodeURIComponent(templateId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(blueprint)
      }
    );
    if (!res.ok) {
      throw await apiResponseError(res, 'Failed to save the bank assessment blueprint');
    }
    return res.json();
  },

  searchItemBank: async ({
    search = '',
    questionType = '',
    area = '',
    areaReview = '',
    topic = '',
    objective = '',
    limit = 25,
    offset = 0,
    signal
  } = {}) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (questionType) params.set('questionType', questionType);
    if (area) params.set('area', area);
    if (areaReview) params.set('areaReview', areaReview);
    if (topic) params.set('topic', topic);
    if (objective) params.set('objective', objective);
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    const res = await authFetch(`${API_BASE_URL}/item-bank/items?${params.toString()}`, {
      cache: 'no-store',
      signal
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to search the Master Item Bank');
    }
    return res.json();
  },

  backfillItemBank: async () => {
    const res = await authFetch(`${API_BASE_URL}/item-bank/process-pending`, { method: 'POST' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to process pending assessments');
    }
    return res.json();
  },

  setItemBankIndexApproval: async (assessmentId, { approved, expectedRevision }) => {
    const res = await authFetch(
      `${API_BASE_URL}/item-bank/onboarding/${encodeURIComponent(assessmentId)}/approval`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved, expectedRevision })
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to update assessment indexing approval');
    }
    return res.json();
  },

  retryItemBankIndex: async assessmentId => {
    const res = await authFetch(
      `${API_BASE_URL}/item-bank/onboarding/${encodeURIComponent(assessmentId)}/retry`,
      { method: 'POST' }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to retry assessment indexing');
    }
    return res.json();
  },

  reindexItemBankAssessment: async (assessmentId) => {
    const res = await authFetch(`${API_BASE_URL}/item-bank/reindex/${encodeURIComponent(assessmentId)}`, {
      method: 'POST'
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to reindex assessment');
    }
    return res.json();
  },

  deleteAssessment: async (id) => {
    const res = await authFetch(`${API_BASE_URL}/assessments/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error('Failed to delete assessment');
    return res.json();
  },

  clearAssessmentRecord: async (id) => {
    const canonicalId = assessmentIdOf(id);
    if (!canonicalId) throw new Error('assessmentId is required');
    const res = await authFetch(
      `${API_BASE_URL}/assessments/${encodeURIComponent(canonicalId)}/clear`,
      { method: 'DELETE' }
    );
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.error || `Failed to clear assessment (${res.status})`);
    }
    return res.json();
  },

  /* ----------------------------------------------------- */
  /*  7) ASSIGN / MISC                                     */
  /* ----------------------------------------------------- */
  getAssessmentAssignments: async (assessmentId) => {
    const canonicalId = assessmentIdOf(assessmentId);
    if (!canonicalId) throw new Error('assessmentId is required');
    const res = await authFetch(
      `${API_BASE_URL}/assessment-settings/assignments/${encodeURIComponent(canonicalId)}`
    );
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.error || `Failed to load assignments (${res.status})`);
    }
    return res.json();
  },

  getAssessmentAssignmentTargets: async (assessmentId) => {
    const canonicalId = assessmentIdOf(assessmentId);
    if (!canonicalId) throw new Error('assessmentId is required');
    const res = await authFetch(
      `${API_BASE_URL}/assessment-settings/assignment-targets/${encodeURIComponent(canonicalId)}`
    );
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.error || `Failed to load assignment targets (${res.status})`);
    }
    return res.json();
  },

  cleanupInactiveUsers: async (options = {}) => {
    const res = await authFetch(`${API_BASE_URL}/users/cleanup-inactive`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options)
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.error || `Inactive-user cleanup failed (${res.status})`);
    }
    return res.json();
  },

  getQuestionHistoryStats: async (filters = {}) => {
    const res = await authFetch(`${API_BASE_URL}/question-history/stats`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filters)
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.error || `Failed to fetch question-history statistics (${res.status})`);
    }
    return res.json();
  },

  getCleanupStatus: async () => {
    const res = await authFetch(`${API_BASE_URL}/cleanup/status`);
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.error || `Failed to fetch cleanup status (${res.status})`);
    }
    return res.json();
  },

  setCleanupEnabled: async (enabled) => {
    const res = await authFetch(`${API_BASE_URL}/cleanup/toggle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: Boolean(enabled) })
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.error || `Failed to update cleanup status (${res.status})`);
    }
    return res.json();
  },

  bulkAssignAssessment: async (assessmentId, changes) => {
    const canonicalId = assessmentIdOf(assessmentId);
    if (!canonicalId || !Array.isArray(changes)) {
      throw new Error('assessmentId and changes[] are required');
    }
    const res = await authFetch(`${API_BASE_URL}/assessment-settings/bulk-assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assessmentId: canonicalId, changes })
    });
    if (!res.ok) {
      const payload = await res.json().catch(() => ({}));
      const error = new Error(payload.error || `Assignment failed (${res.status})`);
      error.code = payload.code || '';
      error.userId = payload.userId;
      error.currentRevision = payload.currentRevision;
      error.assignmentCommitted = Boolean(payload.assignmentCommitted);
      error.status = res.status;
      throw error;
    }
    return res.json();
  },

  assignAssessmentToUsers: async (data) => {
    if (!data.assessmentId || !Array.isArray(data.userIds) || !data.userIds.length)
      throw new Error('assessmentId and userIds[] required');
    const targets = await api.getAssessmentAssignmentTargets(data.assessmentId);
    const revisionByUser = new Map(
      (targets.users || []).map(user => [Number(user.id), Number(user.settingRevision || 0)])
    );
    return api.bulkAssignAssessment(
      data.assessmentId,
      data.userIds.map(userId => ({
        userId,
        assigned: true,
        expectedRevision: revisionByUser.get(Number(userId)) ?? 0
      }))
    );
  },

  getAssignedUsers: async (assessmentId) => {
    const result = await api.getAssessmentAssignments(assessmentId);
    return result.userIds || [];
  },

  /* ----------------------------------------------------- */
  /*  8) SESSIONS                                          */
  /* ----------------------------------------------------- */
  // ✅ SECURITY: Enhanced getSessions with proper access control
  getSessions: async (filters = {}) => {
  // Admin gets all sessions via GET
  if (__adminKey) {
    const res = await authFetch(`${API_BASE_URL}/sessions`);
    if (!res.ok) throw new Error('Failed to fetch sessions');
    return res.json();
  }
  
  // The server derives the teacher's exact main/subuser scope from explicit
  // parent relationships. Never send a caller-authored list of tenant codes.
  const accessCode = filters.accessCode || localStorage.getItem('accessCode');
  if (!accessCode) {
    throw new Error('Access code is required for session access');
  }

  const res = await authFetch(`${API_BASE_URL}/sessions/filter`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ 
      ...filters,
      accessCode
    })
  });
  
  if (res.status === 404) return [];
  if (!res.ok) throw new Error('Failed to fetch sessions');
  return res.json();
},

  // One response carries both rows and summary counts from the same server
  // observation. The dashboard validates this contract before displaying it.
  getLiveSessionItem: async (sessionId, { position, signal } = {}) => {
    const query = position == null ? '' : `?position=${encodeURIComponent(position)}`;
    const res = await authFetch(`${API_BASE_URL}/sessions/${encodeURIComponent(sessionId)}/live-item${query}`, { cache: 'no-store', signal });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const error = new Error(body.error || 'Unable to inspect this session');
      error.status = res.status;
      throw error;
    }
    return res.json();
  },

  getSessionMonitorSnapshot: async (filters = {}) => {
    let res;
    if (__adminKey || filters.admin === true) {
      res = await authFetch(`${API_BASE_URL}/sessions/monitor`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
    } else {
      const accessCode = filters.accessCode || localStorage.getItem('accessCode');
      if (!accessCode) throw new Error('Access code is required for session monitoring');
      res = await authFetch(`${API_BASE_URL}/sessions/monitor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        body: JSON.stringify({ accessCode })
      });
    }
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.error || `Failed to fetch session monitor snapshot (${res.status})`);
    }
    return res.json();
  },

  createSession: async (data) => {
    if (!data.accessCode || !data.learnerAlias || !data.assessmentId)
      throw new Error('Missing required session data');

    const res = await authFetch(`${API_BASE_URL}/sessions`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Failed to create session (${res.status}) ${text}`);
    }
    return res.json();
  },

  updateSession: async (id, data) => {
    const accessCode = data.accessCode || localStorage.getItem('accessCode');
    const res = await authFetch(`${API_BASE_URL}/sessions/${id}`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Failed to update session (${res.status}) ${text}`);
    }
    return res.json();
  },

  abandonSession: async (sessionId, reason) => {
    const res = await authFetch(`${API_BASE_URL}/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) throw new Error(`Failed to abandon session (${res.status})`);
    return res.json();
  },

  dismissAbandonedSession: async (sessionId) => {
    const res = await authFetch(`${API_BASE_URL}/sessions/${sessionId}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error(`Failed to remove abandoned session (${res.status})`);
    const result = await res.json();
    if (!result.removed_from_monitor) {
      throw new Error('Session must be abandoned before it can be removed from the Sessions list');
    }
    return result;
  },

  // Backward-compatible name for older callers. The server preserves the
  // record and performs an idempotent Abandoned transition.
  deleteSession: async (continueCode) => {
    const isCode = /^\d{5}$/.test(continueCode);

    if (isCode) {
      const sessions = await api.getSessions();
      const session = sessions.find((s) => s.continue_code === continueCode);
      if (!session)
        return { success: false, message: 'No session found with that code' };

      continueCode = session.id;
    }

    return api.abandonSession(continueCode);
  },

  // ✅ SECURITY: Enhanced resumeSession with better error handling
  resumeSession: async (data) => {
    if (!data.continueCode || !data.accessCode) {
      throw new Error('continueCode and accessCode required');
    }

    try {
      // ✅ SECURITY: Direct call to server resume endpoint with auth headers
      const res = await authFetch(`${API_BASE_URL}/sessions/resume`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          continueCode: data.continueCode,
          accessCode: data.accessCode,
          // Only include lastQuestionIndex if explicitly provided (for progress updates)
...(data.lastQuestionIndex !== undefined && { lastQuestionIndex: data.lastQuestionIndex }),
        }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ Server resume error:', errorText);
        let errorPayload = {};
        try { errorPayload = JSON.parse(errorText); } catch { errorPayload = {}; }
        const serverMessage = String(errorPayload.error || '').trim();
        
        // ✅ SECURITY: Better error handling with specific messages
        if (res.status === 404) {
          throw new Error('No session found with that continue code. Please check your code and try again.');
        } else if (res.status === 400) {
          throw new Error('Invalid continue code or access code. Please verify and try again.');
        } else if (res.status === 401 || res.status === 403) {
          throw new Error('Access denied. Please check your access code and try again.');
        } else if (res.status === 409) {
          const conflict = new Error(serverMessage || 'This saved assessment cannot be resumed until the conflict is resolved.');
          conflict.status = 409;
          conflict.code = errorPayload.code || 'RESUME_CONFLICT';
          throw conflict;
        } else {
          throw new Error(`Failed to resume session: ${errorText}`);
        }
      }

      const resumeData = await res.json();

      // ✅ SECURITY: Handle the server response structure properly
      if (!resumeData.success) {
        throw new Error('Server failed to resume session properly');
      }

      // Resume returns the exact answer-free catalog row that it resolved and
      // authorized in the same transaction. Retain a fallback only for older
      // servers during a rolling deployment.
      let assessment = resumeData.assessment || null;
      if (!assessment && resumeData.session?.assessment_id) {
        try {
          assessment = await api.getAssessment(resumeData.session.assessment_id);
          if (!assessment) {
            throw new Error('Assessment not found for session');
          }
        } catch (assessmentError) {
          console.error('Error fetching assessment:', assessmentError);
          throw new Error('Failed to load assessment data for session');
        }
      } else if (!assessment) {
        throw new Error('Session missing assessment information');
      }

      const restoredTimerSeconds = Math.max(
        0,
        Math.min(300, Number.parseInt(resumeData.timer_seconds, 10) || 0)
      );
      const restoredTimerMode = normalizeTimerMode(
        resumeData.timer_mode,
        restoredTimerSeconds
      );
      const restoredTtsEnabled = isEnabledFlag(resumeData.tts_enabled) ? 1 : 0;
      const resumedAssessment = {
        ...assessment,
        // Native bank assessments can publish a newer catalog form after this
        // attempt started. Resume must load the immutable file snapshot stored
        // on the performance, never the catalog's newer filename.
        fileName: resumeData.assessment_file_snapshot || assessment.fileName,
        // A resumed attempt keeps the settings captured by its performance.
        // This also supplies settings that the ordinary catalog-by-ID response
        // intentionally does not expose for every caller role.
        min_question_time: restoredTimerSeconds,
        timer_mode: restoredTimerMode,
        timing_policy: resumeData.timing_policy || null,
        item_timing: resumeData.item_timing || {},
        writing_drafts: resumeData.writing_drafts || {},
        overall_time_limit_seconds: boundedOverallTimeSeconds(resumeData.overall_time_limit_seconds),
        overall_deadline_at: resumeData.overall_deadline_at || null,
        overall_remaining_seconds: boundedOverallTimeSeconds(resumeData.overall_remaining_seconds),
        overall_time_expired: Boolean(resumeData.overall_time_expired),
        server_time: resumeData.server_time || null,
        tts_enabled: restoredTtsEnabled,
        display_profile: resumeData.display_profile_snapshot || assessment.display_profile || 'classic',
      };

      // ✅ SECURITY: Return comprehensive session data
      return {
        success: true,
        session: resumeData.session,
        assessment: resumedAssessment,
        student_name: resumeData.session?.student_name,
        actual_grade_level: resumeData.session?.actual_grade_level,
        assessment_id: resumeData.session?.assessment_id,
        fileName: resumedAssessment.fileName || '',
        name: resumedAssessment.name || '',
        last_question_index: resumeData.session?.last_question_index || 1,
        resuming: true,
        continueCode: data.continueCode,
        performanceId: resumeData.performanceId || null,
        timer_seconds: restoredTimerSeconds,
        timer_mode: restoredTimerMode,
        timing_policy: resumeData.timing_policy || null,
        item_timing: resumeData.item_timing || {},
        writing_drafts: resumeData.writing_drafts || {},
        overall_time_limit_seconds: boundedOverallTimeSeconds(resumeData.overall_time_limit_seconds),
        overall_deadline_at: resumeData.overall_deadline_at || null,
        overall_remaining_seconds: boundedOverallTimeSeconds(resumeData.overall_remaining_seconds),
        overall_time_expired: Boolean(resumeData.overall_time_expired),
        server_time: resumeData.server_time || null,
        tts_enabled: restoredTtsEnabled,
        settings_source: resumeData.settings_source || 'current-assignment',
        assessment_file_snapshot: resumeData.assessment_file_snapshot || null,
        display_profile_snapshot: resumeData.display_profile_snapshot || resumedAssessment.display_profile,
        viewEpoch: resumeData.viewEpoch || resumeData.view_epoch || resumeData.session?.current_view_epoch || '',
      };

    } catch (error) {
      console.error('🚨 Resume session error:', error);
      
      throw error;
    }
  },

  // ✅ SECURITY: Fallback method for backward compatibility
  resumeSessionFallback: async (data) => {
    try {
      // Get sessions and try to find matching one
      const sessions = await api.getSessions({ accessCode: data.accessCode });
       
      if (sessions.length === 0) {
        throw new Error('No sessions found for this access code');
      }

      const match = sessions.find((s) => s.continue_code === data.continueCode);
      if (!match) {
        throw new Error('No session found with that continue code. Available sessions: ' + 
          sessions.length + '. Please check your continue code.');
      }

      let assessment = await api.getAssessment(match.assessment_id);
      if (!assessment) {
        throw new Error('Assessment not found for session');
      }

      const last = match.last_question_index || parseInt(data.lastQuestionIndex) || 1;

      // Try to update the session on server
      let updateData = null;
      try {
        const updateRes = await authFetch(`${API_BASE_URL}/sessions/resume`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            continueCode: data.continueCode,
            accessCode: data.accessCode,
            lastQuestionIndex: last,
          }),
        });
        
        if (updateRes.ok) {
          updateData = await updateRes.json();
        }
      } catch (updateError) {
        console.warn('⚠️ Could not update session on server:', updateError);
        // Continue anyway since we found the session
      }

      if (updateData?.assessment_file_snapshot) {
        assessment = { ...assessment, fileName: updateData.assessment_file_snapshot };
      }

      return {
        success: true,
        session: match,
        assessment,
        student_name: match.student_name,
        actual_grade_level: match.actual_grade_level,
        assessment_id: match.assessment_id,
        fileName: assessment.fileName || '',
        name: assessment.name || '',
        last_question_index: last,
        resuming: true,
        continueCode: data.continueCode,
        performanceId: null, // Will be found later in the process
        viewEpoch: updateData?.viewEpoch || updateData?.view_epoch || updateData?.session?.current_view_epoch || '',
      };
    } catch (error) {
      console.error('🚨 Fallback resume also failed:', error);
      throw new Error('Could not resume session: ' + error.message);
    }
  },

  // ✅ NEW: Save pooled question IDs to server session
  savePooledQuestions: async (continueCode, accessCode, pooledQuestionIds) => {
    const res = await authFetch(`${API_BASE_URL}/sessions/update-pooled-questions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ continueCode, accessCode, pooledQuestionIds })
    });
    return res.json();
  },

  // ✅ NEW: Get pooled question IDs from server session
  getPooledQuestions: async (continueCode, accessCode) => {
    const res = await authFetch(`${API_BASE_URL}/sessions/pooled-questions/${continueCode}`);
    return res.json();
  },

  updateSessionProgress: async (continueCode, accessCode, index, activityType = 'navigation', viewEpoch = '') => {
    if (!continueCode || !accessCode) {
      throw new Error('Continue code and access code are required for session progress');
    }
    const questionIndex = Number(index);
    if (!Number.isInteger(questionIndex) || questionIndex < 0 || questionIndex > 100000) {
      throw new Error('Question index must be an integer from 0 through 100000');
    }
    const normalizedActivity = String(activityType || 'navigation').trim().toLowerCase();
    if (!['navigation', 'view', 'heartbeat'].includes(normalizedActivity)) {
      throw new Error('Progress activity must be navigation, view, or heartbeat');
    }
    return enqueueSessionProgress({
      continueCode,
      accessCode,
      questionIndex,
      activityType: normalizedActivity,
      viewEpoch: String(viewEpoch || '').trim()
    });
  },

  scoreOralReading: async ({ performanceId, questionNumber, scoring }) => {
    const res = await authFetch(`${API_BASE_URL}/oral-reading/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ performanceId, questionNumber, scoring })
    });
    const result = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(result.error || 'Unable to save oral-reading score');
    return result;
  },

  patchSession: async (continueCode, data) => api.updateSessionProgress(
    continueCode,
    data.accessCode || localStorage.getItem('accessCode'),
    data.lastQuestionIndex ?? 0,
    data.activityType || 'navigation',
    data.viewEpoch || ''
  ),
};

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    api.flushResponseOutbox().catch(error => {
      console.warn('Protected responses remain queued after reconnect:', error);
    });
  });
}

/* ────────────────────────────────────────────────────────── */
/*  End of file                                              */
/* ────────────────────────────────────────────────────────── */
